import { FC, memo, useCallback, useState } from 'react';
import { Form, Input, message, Modal, Typography } from 'antd';
import { useUIDispatchContext, useModalStateContext } from 'providers/UI';
import { useTranslation } from 'react-i18next';
import { updatePassword } from 'services/api/profile';

import { showRestError } from 'utils/showErrorMessage';

const { Title } = Typography;

const PasswordModal: FC = () => {
  // translations
  const { t } = useTranslation();
  // context
  const { password } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();
  // form
  const [form] = Form.useForm();
  const [isUpdatingPassword, setUpdatingPassword] = useState(false);

  const closeModal = useCallback(() => {
    toggleModal('password', false);
    form.resetFields();
  }, [form, toggleModal]);

  const onPasswordFinish = useCallback(
    (values: { current_password: string; password: string }) => {
      setUpdatingPassword(true);
      updatePassword({
        currentPassword: values.current_password,
        newPassword: values.password
      })
        .then(() => {
          message.success(t('messages.successfully_updated'));
          closeModal();
        })
        .catch(err => showRestError(err))
        .finally(() => setUpdatingPassword(false));
    },
    [closeModal, t]
  );

  return (
    <Modal
      open={password.visible}
      confirmLoading={isUpdatingPassword}
      onCancel={closeModal}
      onOk={form.submit}
    >
      <Title level={4}>{t('profile.change_password')}</Title>
      <Form
        form={form}
        onFinish={onPasswordFinish}
        layout="vertical"
        size="large"
      >
        <Form.Item
          rules={[{ required: true }]}
          name="current_password"
          label={t('profile.password')}
        >
          <Input type="password" maxLength={255} />
        </Form.Item>
        <Form.Item
          rules={[{ required: true }]}
          name="password"
          label={t('profile.new_password')}
        >
          <Input type="password" maxLength={255} />
        </Form.Item>
        <Form.Item
          name="confirm_password"
          label={t('profile.confirm_password')}
          rules={[
            {
              required: true
            },
            {
              message: t('profile.passwords_not_match'),
              validator: (_, value) => {
                if (value === form.getFieldValue('password')) {
                  return Promise.resolve();
                }

                return Promise.reject();
              }
            }
          ]}
        >
          <Input type="password" maxLength={255} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default memo(PasswordModal);
